/**
 * Main/backup diagnostics on the wire (PLAN §8.6: "shows which node is active
 * per production"; wave 7b-tail C). Three layers:
 *
 *   - a NODE's report of its half of the pair (`nodePairReportSchema`): the
 *     peer link, the witnesses, and per production its process's role and
 *     stream health — the standby's lag behind the active, its last snapshot,
 *     gaps and resyncs; the active's acks. It rides the node's 30 s health
 *     report to the cloud (`statusPayloadSchema.pair`, the site link) and the
 *     peer link (`GET /peer/v1/report`).
 *   - a SITE's overview (`siteRedundancySchema`): every production with its
 *     active and standby node, graded OK / WARN / EMERGENCY with a short
 *     reason, and every node with the productions it runs active and standby.
 *     Built by `@acp/handlers/pairOverview.js` from the two nodes' reports — on a
 *     node from its own and its peer's, in the cloud from the last reports the
 *     site link stored — so both hosts answer `getRedundancyOverview` alike.
 *   - the emergency activation (`forcePromoteRequestSchema`): an operator's
 *     forced promotion of a standby whose active node AND every witness are
 *     unreachable (docs/redundancy.md, *Emergency activation*).
 *
 * Durations are "ago" (ms) as the reporting host measured them on its own
 * clock, so a reader never compares two clocks.
 */
import { ulidSchema } from '@acp/domain/primitives.js';
import { z } from 'zod';
import { playoutStateSchema } from './playout.js';
import { seededDeviceSchema } from './redundancy.js';
import { witnessInForceSchema } from './witness.js';

/**
 * The failover window a silent stream is judged against where the node's own timings are unknown (the cloud's
 * overview): `@acp/redundancy/timings.js`' default `heartbeatLossMs` (PLAN §8.4: "~1 s"), held equal by its test.
 */
export const PAIR_HEARTBEAT_LOSS_MS = 1_000;

const nodeIdSchema = z.string().min(1).max(128);
const agoMsSchema = z.number().int().nonnegative();

/** The engine-state stream as the STANDBY sees it. */
export const standbyStreamSchema = z
  .object({
    connected: z.boolean(),
    /** It holds a state from the active (a snapshot applied). */
    synced: z.boolean(),
    /** Engine changes the active has named that the standby does not hold yet; null when unknown (an older process). */
    lagSeqs: z.number().int().nonnegative().nullable(),
    /** Since the active was last heard on the stream (it heartbeats every 150 ms). */
    lastHeardAgoMs: agoMsSchema.nullable(),
    /** Since the last whole state (snapshot or handover) arrived. */
    lastSnapshotAgoMs: agoMsSchema.nullable(),
    /** Gaps detected since it started following this active (each asked for a snapshot). */
    gaps: z.number().int().nonnegative(),
    /** Whole states received after the first (a gap healed, a redial). */
    resyncs: z.number().int().nonnegative(),
  })
  .strict();
export type StandbyStream = z.infer<typeof standbyStreamSchema>;

/** The engine-state stream as the ACTIVE sees it: who follows, and their acks. */
export const activeStreamSchema = z
  .object({
    standbys: z.number().int().nonnegative(),
    /** Since the last ack from a standby. */
    lastAckAgoMs: agoMsSchema.nullable(),
    /** Engine changes not acked yet; null when unknown (an older process). */
    lagSeqs: z.number().int().nonnegative().nullable(),
  })
  .strict();
export type ActiveStream = z.infer<typeof activeStreamSchema>;

/** One production as one node of the pair sees it. */
export const productionPairReportSchema = z
  .object({
    productionId: ulidSchema,
    /** This node's process: active, standby, or none running here. */
    role: z.enum(['active', 'standby']).nullable(),
    term: z.number().int().nonnegative(),
    /** The node the claim names active. */
    activeNode: nodeIdSchema.nullable(),
    manualOnly: z.boolean(),
    /** This node's process is an active muted by the lease (isolated from the peer and every witness). */
    fenced: z.boolean(),
    standby: standbyStreamSchema.nullable(),
    active: activeStreamSchema.nullable(),
    /**
     * How long the stream has been out of sync as this node sees it, or null while it is in sync (or there is no
     * standby to be in sync with): as the active, a standby follows but its acks stopped; as the standby, it is
     * connected but holds no state, or has not heard the active past the heartbeat-loss window.
     */
    outOfSyncMs: agoMsSchema.nullable(),
    alarm: z.string().max(500).nullable(),
  })
  .strict();
export type ProductionPairReport = z.infer<typeof productionPairReportSchema>;

export const pairWitnessSchema = z
  .object({
    kind: z.enum(['lan', 'cloud']),
    reachable: z.boolean(),
    lastContactAgoMs: agoMsSchema.nullable(),
  })
  .strict();

/** A node's half of the pair. */
export const nodePairReportSchema = z
  .object({
    nodeId: nodeIdSchema,
    nodeRole: z.enum(['main', 'backup']),
    peerNodeId: nodeIdSchema,
    /** The peer link (its long poll answered within the lease window). */
    peerLink: z.enum(['connected', 'disconnected']),
    peerLastContactAgoMs: agoMsSchema.nullable(),
    /** Which witness confirms failovers here (D30). */
    witness: witnessInForceSchema,
    witnesses: z.array(pairWitnessSchema).max(8),
    productions: z.array(productionPairReportSchema).max(200),
  })
  .strict();
export type NodePairReport = z.infer<typeof nodePairReportSchema>;

// ─── the site overview ───

export const pairGradeSchema = z.enum(['ok', 'warn', 'emergency']);
export type PairGrade = z.infer<typeof pairGradeSchema>;

/**
 * Why a production has its grade (`@acp/handlers/pairOverview.js` decides, worst first):
 *
 *   emergency  active-lost (neither node reports its process active), isolated (the active is muted by the lease)
 *   warn       active-unreported (its node is silent but the standby hears its stream), standby-missing, stream-lost,
 *              syncing, witness-lost (automatic failover would hold), peer-link-down
 *   ok         in-sync, not-running (not started on the pair yet), no-pair (a node without a peer)
 */
export const pairReasonSchema = z.enum([
  'active-lost',
  'isolated',
  'active-unreported',
  'standby-missing',
  'stream-lost',
  'syncing',
  'witness-lost',
  'peer-link-down',
  'in-sync',
  'not-running',
  'no-pair',
]);
export type PairReason = z.infer<typeof pairReasonSchema>;

export const sitePairNodeSchema = z
  .object({
    nodeId: nodeIdSchema,
    label: z.string().max(200),
    /** Its place in the site (D21); null for a node that reported no pair. */
    nodeRole: z.enum(['main', 'backup']).nullable(),
    /** It reported recently (the cloud: its link is up and its last report is fresh; a node: itself, or its peer answered). */
    online: z.boolean(),
    /** Since its report was taken (0 for the answering node itself); null when it never reported. */
    reportedAgoMs: agoMsSchema.nullable(),
    peerLink: z.enum(['connected', 'disconnected']).nullable(),
    /** Since the peer last answered, as this node measured it (the server list's hover); absent from an older host. */
    peerLastContactAgoMs: agoMsSchema.nullable().optional(),
    witness: witnessInForceSchema.nullable(),
    witnesses: z.array(pairWitnessSchema).max(8),
    /** The productions its process runs ACTIVE, and STANDBY. */
    active: z.array(ulidSchema),
    standby: z.array(ulidSchema),
  })
  .strict();
export type SitePairNode = z.infer<typeof sitePairNodeSchema>;

export const sitePairProductionSchema = z
  .object({
    productionId: ulidSchema,
    /** The rundown's or playlist's name (the domain's `nameSchema`: up to 500). */
    name: z.string().max(500).nullable(),
    studioId: z.string().max(128).nullable(),
    activeNode: nodeIdSchema.nullable(),
    standbyNode: nodeIdSchema.nullable(),
    term: z.number().int().nonnegative(),
    manualOnly: z.boolean(),
    grade: pairGradeSchema,
    reason: pairReasonSchema,
    /** One line for people: what is wrong, or that all is well. */
    detail: z.string().max(500),
    /** The standby's stream, as the standby node reported it. */
    stream: standbyStreamSchema.nullable(),
    /** The active's acks, as the active node reported them. */
    acks: activeStreamSchema.nullable(),
    /** How long it has been out of sync (the active node's measure, else the standby's); null while in sync. */
    outOfSyncMs: agoMsSchema.nullable(),
    /** The active node's alarm for it, else the standby node's. */
    alarm: z.string().max(500).nullable(),
  })
  .strict();
export type SitePairProduction = z.infer<typeof sitePairProductionSchema>;

export const siteRedundancySchema = z
  .object({
    siteId: z.string().min(1).max(128),
    siteName: z.string().max(200).nullable(),
    /** Who assembled it: a node of the site (live, its own view and its peer's) or the cloud (the last 30 s reports). */
    source: z.enum(['node', 'cloud']),
    generatedAt: z.iso.datetime({ offset: true }),
    nodes: z.array(sitePairNodeSchema),
    productions: z.array(sitePairProductionSchema),
  })
  .strict();
export type SiteRedundancy = z.infer<typeof siteRedundancySchema>;

export const redundancyOverviewQuerySchema = z
  .object({ siteId: z.string().min(1).max(128).optional().describe('Only this site (the default: every site the caller may see).') })
  .strict();
export const redundancyOverviewResponseSchema = z.object({ sites: z.array(siteRedundancySchema) }).strict();
export type RedundancyOverview = z.infer<typeof redundancyOverviewResponseSchema>;

// ─── the standby's view ───

export const standbyPlayoutResponseSchema = z
  .object({
    productionId: ulidSchema,
    /** The node whose standby process this state comes from; null when no standby is known. */
    standbyNode: nodeIdSchema.nullable(),
    /**
     * What the standby currently believes (its engine continues from every state the active streams): on air, next,
     * its devices muted. Null when there is no standby, or it could not be asked (`reason`).
     */
    state: playoutStateSchema.nullable(),
    /** When the state was taken (the cloud: when the standby last reported it). */
    asOf: z.iso.datetime({ offset: true }).nullable(),
    reason: z.string().max(500).nullable(),
  })
  .strict();
export type StandbyPlayout = z.infer<typeof standbyPlayoutResponseSchema>;

// ─── emergency activation ───

export const forcePromoteRequestSchema = z
  .object({
    /** The production's name, typed by the operator: the explicit confirmation. */
    confirm: z.string().min(1).max(500),
    /** Why (kept in the audit entry and the tenant alert). */
    reason: z.string().trim().min(1).max(500),
  })
  .strict();
export type ForcePromoteRequest = z.infer<typeof forcePromoteRequestSchema>;

export const forcePromoteResultSchema = z
  .object({
    productionId: ulidSchema,
    /** The node the claim named active before (unreachable). */
    from: nodeIdSchema.nullable(),
    /** This node: active now. */
    to: nodeIdSchema,
    /** The new term: the old active mutes the moment it sees it (D9's fencing). */
    term: z.number().int().nonnegative(),
    durationMs: z.number().nonnegative(),
    devices: z.array(seededDeviceSchema),
    pendingDevices: z.array(z.string().max(128)),
    /** What the decision rested on (the peer unreachable, what each witness said). */
    evidence: z.string().max(1000),
    /** The split-brain warning, repeated in the answer. */
    warning: z.string().max(1000),
  })
  .strict();
export type ForcePromoteResult = z.infer<typeof forcePromoteResultSchema>;
